const YOUTUBE_ID = /^[\w-]{11}$/;
const VIMEO_ID = /^\d+$/;

const parseUrl = (value: string): URL | null => {
  try {
    return new URL(value.trim());
  } catch {
    return null;
  }
};

/**
 * Privacy-friendly embed URL for a YouTube or Vimeo page/embed/share link, or
 * null for anything else (the player then shows a plain link).
 */
export const videoEmbedUrl = (value: string): string | null => {
  const url = parseUrl(value);
  if (!url || url.protocol !== "https:") return null;
  const host = url.hostname.replace(/^(www\.|m\.)/, "");
  const segments = url.pathname.split("/").filter(Boolean);

  let youtubeId: string | null | undefined = null;
  if (host === "youtu.be") youtubeId = segments[0];
  else if (host === "youtube.com" || host === "youtube-nocookie.com") {
    youtubeId =
      segments[0] === "watch"
        ? url.searchParams.get("v")
        : ["embed", "shorts", "live", "v"].includes(segments[0] ?? "")
          ? segments[1]
          : null;
  }
  if (youtubeId && YOUTUBE_ID.test(youtubeId)) {
    return `https://www.youtube-nocookie.com/embed/${youtubeId}`;
  }

  if (host === "vimeo.com" || host === "player.vimeo.com") {
    // vimeo.com/123, vimeo.com/channels/x/123, player.vimeo.com/video/123
    const vimeoId = segments.findLast((segment) => VIMEO_ID.test(segment));
    if (vimeoId) return `https://player.vimeo.com/video/${vimeoId}`;
  }
  return null;
};

/**
 * Href that is safe to render from author content: http(s)/mailto URLs or a
 * site path. Anything else (javascript:, data:, protocol-relative) → null.
 */
export const safeHref = (value: string): string | null => {
  const trimmed = value.trim();
  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && !trimmed.startsWith("/\\")) {
    return trimmed;
  }
  const url = parseUrl(trimmed);
  if (!url) return null;
  return ["http:", "https:", "mailto:"].includes(url.protocol) ? url.href : null;
};
